import {Injectable} from '@angular/core';
import {BackendService} from '@tg4/http-infrastructure/dist/src';
import {Observable, of} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {AuthService} from '../shared/services/auth.service';
import {UserPreview} from '../shared/interfaces/user-preview.interface';
import {map} from 'rxjs/operators';
import {UserRule, UserVisibilitySummary} from './_interfaces/user-visibility';
import {SharedService} from '../shared/services/shared.service';
import { ConfigService } from '../shared/services/multi-clients/config.service';

@Injectable()
export class UsersService {
    apiUrl: any;
    importVisibilityParticipants(cycleId: number, userId: string, data: string): Observable<any> {
        return this._httpService.post('importVisibilityParticipants', {
            'data': data, 'cycleId': cycleId, 'userId': userId
        });
    }

    constructor(private _httpService: BackendService,
                private authService: AuthService,
                private http: HttpClient,
                private _sharedService: SharedService,
                private _configService: ConfigService
            ) {
        this.apiUrl = this._configService.settings.apiUrl;
    }

    public getUsers(page: number = 1, pageSize: number = 10, search = ''): Observable<any> {
        return this._httpService.get('getUsers', [], [
            {'name': 'page', 'value': page as any},
            {'name': 'pageSize', 'value': pageSize as any},
            {'name': 'search', 'value': search as any}
        ]);
    }
    
    public getUser(id: string): Observable<any> {
        return this._httpService.get('getUser', [], [
            {'name': 'userId', 'value': id as any}
        ]);
    }

    public getAllRoles(): Observable<any> {
        return this._httpService.get('getAllRoles', [], []);
    }

    public getPagedFilteredUsersSyncProcesses(
        page: number = 1, pageSize: number = 10, fileId: number = null
    ): Observable<any> {
        return this._httpService.post('getFileImports', {
            'page': page,
            'pageSize': pageSize,
            'fileId': fileId
        });
    }

    public postUsersSyncProcess(fileToUpload: any): Observable<any> {
        return this._httpService.post('addUsersSyncProcess', fileToUpload);
    }

    public getUsersVisibility(
        page: number = 1, pageSize: number = 10, searchValue: string = ''
    ): Observable<any> {
        return this._httpService.get('getUsersVisibilityAccesses', [], [
            {'name': 'page', 'value': page as any},
            {'name': 'pageSize', 'value': pageSize as any},
            {'name': 'search', 'value': searchValue as any}
        ]);
    }

    public getUsersVisibilitySummariesForCopy(
        page: number = 1,
        pageSize: number = 10,
        searchValue: string = '',
        excludedUserId: string = ''
    ): Observable<{ data: { users: UserVisibilitySummary[], total: number } }> {
        return this._httpService.get('getUsersWithAttributeVisibilityRules', [], [
            {'name': 'page', 'value': page as any},
            {'name': 'pageSize', 'value': pageSize as any},
            {'name': 'search', 'value': searchValue as any},
            {'name': 'excludedUserId', 'value': excludedUserId as any}
        ]);
    }

    public getUserVisibilityRules(userId: string, searchValue: string = '', userRulesIndex: number, pageSize: number): Observable<any> {
        return this._httpService.get('getUserVisibilityRules', [], [
            {'name': 'userId', 'value': userId as any},
            {'name': 'userRulesIndex', 'value': userRulesIndex as any},
            {'name': 'searchValue', 'value': searchValue as any},
            {'name': 'pageSize', 'value': pageSize as any},
        ]);
    }

    public getLastFrozenStructure(): Observable<any> {
        return this._httpService.get('getLastFrozenStructure', []);
    }

    public addFrozenStructure(newFrozenStructure): Observable<any> {
        return this._httpService.post('addFrozenStructure', newFrozenStructure);
    }

    public updateIndividualVisibilityRules(userId: string, rules: UserRule[]) {
        const data = {userId: userId, cycleParticipantIds: rules.map(x => x.cycleParticipantId)};
        return this._httpService.put('updateIndividualVisibilityRules', data);
    }

    public deleteAttributesVisibilityRule(
        userId: string, ruleId: number
    ): Observable<any> {
        return this._httpService.delete('deleteAttributesVisibilityRule', [
            {'name': 'userId', 'value': userId},
            {'name': 'ruleId', 'value': ruleId as any}
        ]);
    }

    public addAttributesVisibilityRule(
        userId: string,
        ruleName: string,
        attributeConditions: Array<{ includeUsers: boolean, attributeId: number }>
    ): Observable<any> {
        return this._httpService.post('addAttributesVisibilityRule', {
            userId,
            ruleName,
            attributeConditions
        });
    }

    public updateAttributesVisibilityRule(
        userId: string,
        ruleId: number,
        ruleName: string,
        attributeConditions: Array<{ includeUsers: boolean, attributeId: number }>
    ): Observable<any> {
        return this._httpService.put('updateAttributesVisibilityRule', {
            userId,
            ruleId,
            ruleName,
            attributeConditions
        });
    }

    public importAttributesVisibilityRules(
        userId: string,
        rules: Array<{ ruleName: string, attributeConditions: Array<{ includeUsers: boolean, attributeId: number }> }>
    ): Observable<any> {
        return this._httpService.post('importAttributesVisibilityRules', { userId, rules });
    }

    public deleteIndividualVisibilityRules(deleteRequest: {
        userId: string,
        targetUsers: Array<{ cycleId: number, targetUserId: string }>
    }): Observable<any> {
        return this._httpService.post('deleteIndividualVisibilityRules', deleteRequest);
    }

    public createEditUser(user: any) {
        return this._httpService.post('createEditUser', user);
    }

    public addUserAttribute(userId: string, attributeId: number): Observable<any> {
        return this._httpService.post('addUserAttribute', {
            userId: userId,
            attributeId: attributeId
        });
    }

    public removeUserAttribute(userId: string, attributeId: number): Observable<any> {
        return this._httpService.post('removeUserAttribute', {
            userId: userId,
            attributeId: attributeId
        });
    }

    public exportUserVisibility(userId: string): Observable<any> {
        return this.downloadFile({
            userId: userId
        });
    }

    downloadFile(data: any): any {
        const applicationsUrl = `${this.apiUrl}/api/userAccess/exportRules`;
        const user = this.authService.getLoggedUser();
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': user.access_token,
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
            observe: 'response' as 'body',
            responseType: 'blob' as 'json'
        };

        const impersonatedUser: UserPreview | null = this.authService.getImpersonatedUser();
        if (impersonatedUser && impersonatedUser.userId) {
            options.headers['UserIdToImpersonate'] = impersonatedUser.userId;
        }
        return this.http.post(applicationsUrl, data, options)
            .pipe(map((res) => this._sharedService.downloadFile(res)));
    }
}
